import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Award, BookOpen } from "lucide-react";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { studio, officePhotos } from "@/data/site";

export default function ChiSiamo() {
  return (
    <main data-testid="chi-siamo-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="pt-36 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Chi Siamo</SectionLabel>
            <h1 className="font-serif font-medium mt-5 text-5xl md:text-7xl tracking-tight leading-[1.02] text-slate-900">
              Lo studio.<br />
              <em className="not-italic text-amber-800">La persona.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg text-slate-600 leading-relaxed">
              Tre decenni di lavoro accanto a committenti pubblici e privati,
              progettando, rilevando e consigliando con metodo e attenzione.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LO STUDIO */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Lo Studio</SectionLabel>
              <SectionTitle className="mt-4">
                Trent'anni accanto al territorio veneto.
              </SectionTitle>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 grid grid-cols-2 gap-3">
                <div className="aspect-[3/4] img-zoom border border-slate-200">
                  <img src={officePhotos[0]} alt="Ingresso studio" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-[3/4] img-zoom border border-slate-200 mt-10">
                  <img src={officePhotos[2]} alt="Interno studio" className="w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-slate-200">
            <Reveal>
              <p className="text-lg text-slate-700 leading-relaxed">
                Lo studio tecnico <strong>Cecconato Geometra Michele</strong> si
                trova in <strong>Villorba (TV) – via Morganella 4/c</strong>, e
                vanta un'esperienza trentennale negli ambiti della
                progettazione, della consulenza e dei rilievi topografici.
                Opera prevalentemente nel triveneto, in particolare nella
                provincia di Treviso.
              </p>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Lo studio fornisce servizi a committenti pubblici e privati
                nell'ambito civile, rurale, commerciale e industriale.
              </p>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                I prevalenti settori di attività dello studio tecnico,
                garantiti dall'esperienza maturata e dal continuo aggiornamento
                professionale, possono essere sintetizzati in: progettazione e
                pratiche amministrative; topografia e geomatica; cartografia e
                GIS; rilievi con drone (fotogrammetria e LIDAR); rilievi laser
                scanner statici e dinamici; pratiche catastali; certificazione
                energetica; consulenze e gestione immobiliare.
              </p>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                La reciproca collaborazione con altri tecnici, specialisti
                nelle rispettive discipline, permette di svolgere incarichi di
                vario genere garantendo al cliente una completa ed efficace
                soluzione alle specifiche richieste.
              </p>

              <div className="mt-10 p-8 bg-white border-l-4 border-amber-700">
                <div className="overline">Mission</div>
                <p className="mt-4 font-serif text-2xl md:text-3xl text-slate-900 leading-tight">
                  "Individuare la miglior soluzione per soddisfare le esigenze
                  e le necessità del cliente impiegando metodi adeguati e
                  rispettando le tempistiche, senza intaccare la qualità e il
                  risultato finale del lavoro."
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GEOMETRA - Bio */}
      <section className="py-24 lg:py-32 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start relative">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel className="text-amber-400">Note personali</SectionLabel>
              <h2 className="font-serif font-medium mt-5 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Geom. Michele<br /><em className="not-italic text-amber-300">Cecconato</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 aspect-[4/5] img-zoom border border-white/10">
                <img
                  src={officePhotos[3]}
                  alt="Interno studio"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-lg text-slate-200 leading-relaxed">
                Sono nato a Treviso il 14 maggio 1968. Ho conseguito il diploma
                di geometra nel 1987 presso l'Istituto Tecnico
                <em> "A. Palladio"</em> di Treviso.
              </p>
              <p className="mt-6 text-lg text-slate-200 leading-relaxed">
                Portato a termine il praticantato e superato l'esame di Stato,
                nel 1990 mi sono iscritto all'Albo Provinciale dei Geometri
                della Provincia di Treviso al n. 2306 di posizione, iniziando
                fin da subito la libera professione e mantenendo una costante e
                proficua collaborazione con altri studi tecnici.
              </p>
              <p className="mt-6 text-lg text-slate-200 leading-relaxed">
                Sono costantemente informato sulle novità in ambito
                legislativo, sulle ultime tecnologie costruttive e sulle
                innovative tecniche di rilievo, grazie ai continui corsi di
                aggiornamento che ho frequentato e che continuo a frequentare.
              </p>
              <p className="mt-6 text-lg text-slate-200 leading-relaxed">
                A partire dal <strong className="text-amber-300">2005</strong> mi
                occupo della docenza relativa alla materia <em>Topografia</em>{" "}
                nei corsi preparatori all'esame di abilitazione all'esercizio
                della libera professione organizzati dalla Fondazione dei
                Geometri della Provincia di Treviso.
              </p>
            </Reveal>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {[
                { Icon: GraduationCap, h: "1987", p: "Diploma di Geometra · Istituto A. Palladio, Treviso" },
                { Icon: Award, h: "1990", p: `Iscrizione Albo Geometri TV · n. 2306` },
                { Icon: BookOpen, h: "Dal 2005", p: "Docente di Topografia · Fondazione Geometri TV" },
              ].map((b, i) => (
                <Reveal key={b.h} delay={i * 0.1}>
                  <div className="border-t-2 border-amber-400 pt-5">
                    <b.Icon size={22} className="text-amber-300" />
                    <div className="font-serif text-2xl mt-3">{b.h}</div>
                    <div className="text-sm text-slate-300 mt-2 leading-relaxed">{b.p}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-slate-900 leading-tight">
              Vuoi parlare con il geometra?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contatti"
              data-testid="chisiamo-cta-contact"
              className="mt-8 inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-full font-medium hover:bg-amber-800 transition-colors"
            >
              Scrivici <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
